import { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Lion } from "@/components/brand/Lion";
import { Button } from "@/components/ui/Button";
import { Checkbox, Field, Input } from "@/components/ui/Field";
import { formatDateLong, formatInstant, type IsoDate } from "@/lib/dates";
import { supabase } from "@/lib/supabase";

/**
 * The crew member's booking page, /crew/<token>: read the agreement, sign it
 * with their full name, and, the day before, open the game plan and confirm
 * they've read it. No login: the link is the key, like a gallery.
 */

interface Booking {
  role: string;
  status: "draft" | "sent" | "signed" | "released";
  name: string | null;
  agreement: string;
  signedAt: string | null;
  signedName: string | null;
  countersignedAt: string | null;
  countersignedName: string | null;
  releasedAt: string | null;
  releasedReason: string | null;
  eventDate: IsoDate | null;
  gamePlan: string | null;
  briefConfirmedAt: string | null;
}

async function call(body: Record<string, unknown>): Promise<Booking> {
  const { data, error, response } = await supabase.functions.invoke<{
    booking?: Booking;
    error?: string;
  }>("crew-agreement", { body });
  if (!error && data?.booking) return data.booking;
  const res = (error as { context?: Response } | null)?.context ?? response;
  const parsed = (await res?.json().catch(() => null)) as {
    error?: string;
  } | null;
  throw new Error(
    parsed?.error ?? "Something went wrong. Please try again, or tell Jenalee.",
  );
}

export function CrewPage() {
  const { token = "" } = useParams();
  const queryClient = useQueryClient();
  const key = ["crew-booking", token];
  const booking = useQuery({
    queryKey: key,
    queryFn: () => call({ token }),
    retry: false,
    refetchOnWindowFocus: false,
  });
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);

  const act = useMutation({
    mutationFn: (body: Record<string, unknown>) => call({ token, ...body }),
    onSuccess: (data) => queryClient.setQueryData(key, data),
  });

  const download = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke<{ url?: string }>(
        "crew-agreement",
        { body: { token, pdf: true } },
      );
      if (error || !data?.url)
        throw new Error("Your copy could not be made. Please try again.");
      window.location.href = data.url;
    },
  });

  const b = booking.data;

  return (
    <div data-register="portal" className="min-h-dvh bg-paper text-ink">
      <div className="mx-auto flex max-w-[44rem] flex-col gap-8 px-4 py-10 sm:px-6">
        <header className="flex items-center gap-3">
          <Lion size={28} />
          <div>
            <p className="label">De Leeuw Photo &amp; Film</p>
            <p className="text-[length:var(--text-sm)] text-taupe">
              Your booking
              {b?.eventDate ? ` · ${formatDateLong(b.eventDate)}` : ""}
            </p>
          </div>
        </header>

        {booking.isPending ? (
          <p className="text-taupe">Opening your booking…</p>
        ) : null}
        {booking.isError ? (
          <p className="text-clay">{booking.error.message}</p>
        ) : null}

        {b ? (
          <>
            {b.status === "released" ? (
              <p className="card p-4">
                This booking was ended
                {b.releasedAt ? ` on ${formatInstant(b.releasedAt)}` : ""}.
                {b.releasedReason ? ` ${b.releasedReason}` : ""}
              </p>
            ) : null}

            {b.gamePlan ? (
              <section className="card flex flex-col gap-3 p-4">
                <h2 className="label">The game plan</h2>
                <pre className="whitespace-pre-wrap font-[family-name:var(--font-body)] text-[length:var(--text-sm)] leading-relaxed">
                  {b.gamePlan}
                </pre>
                {b.briefConfirmedAt ? (
                  <p className="text-[length:var(--text-sm)] text-sage">
                    You confirmed {formatInstant(b.briefConfirmedAt)}. See you
                    there!
                  </p>
                ) : (
                  <Button
                    variant="primary"
                    loading={act.isPending}
                    onClick={() => act.mutate({ confirmBrief: true })}
                  >
                    I've read it and I'm on for the day
                  </Button>
                )}
              </section>
            ) : null}

            <article className="border-y py-6">
              <pre className="whitespace-pre-wrap font-[family-name:var(--font-body)] text-[length:var(--text-sm)] leading-relaxed">
                {b.agreement}
              </pre>
            </article>

            {b.status === "signed" ? (
              <div className="flex flex-col gap-3 text-[length:var(--text-sm)]">
                <p className="text-sage">
                  Signed by {b.signedName}
                  {b.signedAt ? ` on ${formatInstant(b.signedAt)}` : ""}. Thank
                  you! This booking stands until one of us ends it in writing.
                </p>
                {b.countersignedAt ? (
                  <>
                    <p className="text-sage">
                      Signed for De Leeuw Photo &amp; Film by{" "}
                      {b.countersignedName} on{" "}
                      {formatInstant(b.countersignedAt)}.
                    </p>
                    <div>
                      <Button
                        variant="primary"
                        loading={download.isPending}
                        onClick={() => download.mutate()}
                      >
                        Download your signed copy (PDF)
                      </Button>
                    </div>
                    {download.isError ? (
                      <p className="text-clay">{download.error.message}</p>
                    ) : null}
                  </>
                ) : (
                  <p className="text-taupe">
                    Jenalee signs next. Once she has, your copy with both
                    signatures will be here to download.
                  </p>
                )}
              </div>
            ) : null}

            {b.status === "sent" ? (
              <form
                className="flex flex-col gap-4"
                onSubmit={(event) => {
                  event.preventDefault();
                  act.mutate({ sign: { name, agree } });
                }}
              >
                <Field label="Your full name (name and surname)">
                  <Input
                    value={name}
                    autoComplete="name"
                    onChange={(e) => setName(e.target.value)}
                  />
                </Field>
                <Checkbox
                  label="I have read this agreement and I agree to it."
                  description="Typing your name and signing here is your signature. We record the time and your device's address with it."
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                />
                <div>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={!agree || name.trim().length < 4}
                    loading={act.isPending}
                  >
                    Sign the agreement
                  </Button>
                </div>
              </form>
            ) : null}

            {act.isError ? (
              <p className="text-clay">{act.error.message}</p>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
